import { Box, Divider, Grid, GridItem, Heading, Image, Text } from '@chakra-ui/react';
import { IconRequestContainer } from 'modules/Editor/components/WidgetMenu/SideMenu/IconWidgetMenu/IconRequestContainer';
import { useAppSelector } from 'modules/Editor/store';
import { selectHasRecommendedIconPack } from 'modules/Editor/store/editorSettingsSelector';
import { PermissionBadge, UserType, hasResourcePermission, useUser } from 'modules/User';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { Icon } from './Icon';
import { IconPackData, Icons8IllustrationIconPackData } from './IconView.types';
import { RecommendedIconPack } from './RecommendedIconPack';

const CONTAINER_PADDING_TOP_PX = 16;
const PREVIEW_CONTAINER_WIDTH = 318;

// List size when no recommendation
const LIST_ROW_SIZE = 177;

// Recommended pack row size
const RECOMMENDED_ROW_SIZE = 152;

// Icon pack size when recommendation is present
// First pack will have a header
const ICON_PACK_WITH_HEADER_SIZE = 212;

/**
 * ICON PACK LIST COMPONENT
 *
 * Renders a list of icon pack previews
 */
interface IconPackListProps {
  packs: IconPackData[];
  onSelectPack: (iconPack: IconPackData) => void;
  show: boolean;
  testId: string;
  menuHeight?: number;
  includeRecommended?: boolean;
}

export const IconPackList = memo(({ packs, onSelectPack, show, menuHeight, includeRecommended }: IconPackListProps) => {
  const packCardRefList = useRef<{ [value: number]: HTMLDivElement }>({});
  const { userType } = useUser();
  const style = !show ? { display: 'none' } : {};
  const listRef = useRef<VariableSizeList>(null);
  const firstVisiblePackIdx = useRef<number>(0);

  const hasRecommendedIconPack = useAppSelector(selectHasRecommendedIconPack);
  const showRecommended = includeRecommended && hasRecommendedIconPack;

  const setCardRef = (ref: Element | null, packIdx: number) => {
    if (!ref) return null;
    packCardRefList.current[packIdx] = ref as HTMLDivElement;
  };

  const getItemSize = useCallback(
    (index: number) => {
      if (showRecommended) {
        if (index === 0) return RECOMMENDED_ROW_SIZE + CONTAINER_PADDING_TOP_PX;

        // First pack will have a header
        if (index === 1) return ICON_PACK_WITH_HEADER_SIZE + CONTAINER_PADDING_TOP_PX;
      }
      return LIST_ROW_SIZE + CONTAINER_PADDING_TOP_PX;
    },
    [showRecommended],
  );

  const getItemKey = useCallback(
    (index: number) => {
      if (showRecommended) {
        if (index === 0) return 'recommended';
        if (index === 1) return 'header';
      }

      return packs[index - (showRecommended ? 1 : 0)].id;
    },
    [packs, showRecommended],
  );

  const iconPackListData = useMemo(
    () => ({
      iconPackList: packs,
      userType,
      onClick: onSelectPack,
      setCardRef,
      showRecommended,
    }),
    [packs, userType, onSelectPack, showRecommended],
  );

  // On show, set focus to first visible item
  useEffect(() => {
    if (!show) return;

    packCardRefList.current[firstVisiblePackIdx.current]?.focus();
  }, [show]);

  const onItemsRendered = useCallback(({ visibleStartIndex }: { visibleStartIndex: number }) => {
    firstVisiblePackIdx.current = visibleStartIndex;
  }, []);

  // Ensure list recalculates list size when recommended pack is toggled
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [hasRecommendedIconPack]);

  if (!menuHeight) return null;

  return (
    <VariableSizeList
      itemData={iconPackListData}
      height={menuHeight}
      width='100%'
      itemSize={getItemSize}
      itemCount={showRecommended ? packs.length + 1 : packs.length}
      ref={listRef}
      onItemsRendered={onItemsRendered}
      itemKey={getItemKey}
      style={{
        ...style,
        overflowX: 'hidden',
      }}
    >
      {IconPackCard}
    </VariableSizeList>
  );
});
IconPackList.displayName = 'IconPackList';

type IconPackListComponentData = {
  iconPackList: IconPackData[];
  userType: UserType;
  onClick: (iconPack: IconPackData) => void;
  setCardRef: (ref: Element | null, packIdx: number) => void;
  showRecommended?: boolean;
};

const IconPackCard = memo(({ data, style, index }: ListChildComponentProps<IconPackListComponentData>) => {
  const { iconPackList, userType, onClick, setCardRef, showRecommended } = data;
  const iconPackIdx = showRecommended ? index - 1 : index;
  const setRef = (ref: Element | null) => setCardRef(ref, iconPackIdx);
  const iconPackData = iconPackList[iconPackIdx];
  const isLastCard = data?.iconPackList.length && iconPackIdx === data.iconPackList.length - 1;
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.iconsMenu.mockIconPack',
  });

  if (showRecommended && index === 0) {
    return (
      <Box overflow={'visible'} position={'relative'} style={style} pt={4} pr={4}>
        <RecommendedIconPack onSelectIconPack={onClick} />
      </Box>
    );
  }
  if (!iconPackData) return null;

  const { id: iconPackId, displayIconIds, permission, isIcons8, isIcons8Illustration } = iconPackData;
  const IllustrationPreviewImg = isIcons8Illustration
    ? (iconPackData as Icons8IllustrationIconPackData).previewImg
    : undefined;
  const iconPackListLayoutLength = isIcons8Illustration ? 2 : displayIconIds!.length;

  const handleClickCard = () => {
    onClick(iconPackData);
  };

  const showBadge = !hasResourcePermission(userType, permission);
  const labelKey = iconPackId.toString();
  const label = t(labelKey as keyof typeof t);

  return (
    <Box overflow={'visible'} position={'relative'} style={style} pt={4}>
      {showRecommended && iconPackIdx === 0 && (
        <Heading as='h3' fontSize='md' mb={4} fontWeight='semibold' color='font.500'>
          Collections
        </Heading>
      )}
      <Box
        mr={4}
        aria-label={label}
        borderRadius={'lg'}
        border={'1px solid'}
        borderColor={'gray.100'}
        overflow='visible'
        position='relative'
        ref={setRef}
        as={'button'}
        role={'group'}
        cursor={'pointer'}
        onClick={handleClickCard}
        w={`${PREVIEW_CONTAINER_WIDTH}px`}
      >
        {showBadge && <PermissionBadge permission={permission} position={'absolute'} top={-2} right={2} />}
        <Grid
          gridTemplateColumns={`repeat(${iconPackListLayoutLength / 2}, auto)`}
          p={isIcons8Illustration ? 0 : 3}
          columnGap={4}
          rowGap={2}
          transitionDuration={'200ms'}
          _groupHover={{ bg: 'hover.gray' }}
          h='132px'
          w='full'
        >
          <>
            {!isIcons8Illustration &&
              displayIconIds!.map((iconId) => (
                <GridItem key={iconId} h={50}>
                  <IconCardItem iconId={iconId} isIcons8={isIcons8} />
                </GridItem>
              ))}
            {isIcons8Illustration && (
              <GridItem h={120}>
                <Image src={IllustrationPreviewImg} alt={label} objectFit='contain' />
              </GridItem>
            )}
          </>
        </Grid>
        <Divider />
        <Text fontSize={'xs'} p={3} color={'font.500'} textAlign={'left'}>
          {label}
        </Text>
      </Box>
      {isLastCard && <IconRequestContainer mt={4} mr={1} />}
    </Box>
  );
});

IconPackCard.displayName = 'IconPackCard';

interface IconCardItemProps {
  iconId: string;
  thumbnailUrl?: string;
  isIcons8Illustration?: boolean;
  isIcons8?: boolean;
}
const IconCardItem = ({ iconId, isIcons8, isIcons8Illustration, thumbnailUrl }: IconCardItemProps) => (
  <Icon iconId={iconId} isIcons8={isIcons8} isIcons8Illustration={isIcons8Illustration} thumbnailUrl={thumbnailUrl} />
);

IconCardItem.displayName = 'IconCardItem';
